import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePlan } from '../context/PlanContext';
import {
  X, Sparkles, ShieldCheck, FileText, Brain, ArrowRight, Star,
  Mail, Users, MessageSquare, Check, ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';

const FEATURE_VERSION = 'agentic_personalization_v2.20.0';
const STORAGE_KEY = `star_feature_dismissed_${FEATURE_VERSION}`;

const STEPS = [
  {
    id: 'intro',
    icon: Sparkles,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    title: 'Introducing Agentic Personalization',
    subtitle: 'AI that writes like you',
    description: 'Your campaigns just got a superpower. NurtureCRM now writes unique, personalized emails for every contact — in your authentic voice, adapted to how each person prefers to communicate.',
    highlights: [
      { icon: Mail, text: 'Every email is unique — written from scratch, not templated' },
      { icon: Users, text: 'Adapts to each contact\'s DISC personality profile' },
      { icon: MessageSquare, text: 'References your actual conversation history naturally' },
      { icon: Brain, text: 'Learns from your feedback to get better over time' },
    ],
  },
  {
    id: 'how',
    icon: Brain,
    iconBg: 'bg-blue-100',
    iconColor: 'text-blue-600',
    title: 'Deep Context, Not Templates',
    subtitle: 'The AI reads before it writes',
    description: 'Before writing a single word, the AI assembles four context files about the sender and receiver:',
    contextCards: [
      { label: 'Your Voice', desc: 'Your voice profile — vocabulary, tone, style', color: 'border-violet-200 bg-violet-50' },
      { label: 'Contact Profile', desc: 'DISC personality, preferences, deal history', color: 'border-blue-200 bg-blue-50' },
      { label: 'Company Intel', desc: 'Industry, key people, relationship history', color: 'border-emerald-200 bg-emerald-50' },
      { label: 'Your History', desc: 'Every past email between you and them', color: 'border-amber-200 bg-amber-50' },
    ],
  },
  {
    id: 'review',
    icon: ShieldCheck,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    title: 'You\'re Always in Control',
    subtitle: 'Human-in-the-loop review',
    description: 'Nothing sends without your approval. Every AI draft goes through your review queue where you can:',
    actions: [
      { label: 'Approve & Send', desc: 'One click to send and advance the campaign', color: 'text-green-600' },
      { label: 'Edit & Send', desc: 'Tweak the subject or body, then send', color: 'text-amber-600' },
      { label: 'Regenerate', desc: 'Tell the AI what to change and get a new draft', color: 'text-blue-600' },
      { label: 'Reject', desc: 'Skip with feedback — the AI learns from it', color: 'text-red-500' },
    ],
  },
  {
    id: 'start',
    icon: Star,
    iconBg: 'bg-amber-100',
    iconColor: 'text-amber-600',
    title: 'Ready to Try It?',
    subtitle: 'Get started in 2 minutes',
    description: 'Add an "AI Personalized Email" step to any campaign. Write an objective, set the tone, and let the AI do the rest.',
    getStartedSteps: [
      'Open any campaign in the builder',
      'Add an "AI Personalized Email" step (under Engagement)',
      'Write what the email should accomplish',
      'Enroll contacts — drafts appear in AI Reviews',
    ],
  },
];

function StepDots({ currentStep, totalSteps, onDotClick }) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }).map((_, i) => (
        <button
          key={i}
          onClick={() => onDotClick(i)}
          className={`w-2 h-2 rounded-full transition-all ${
            i === currentStep ? 'bg-violet-600 w-6' : 'bg-stone-300 hover:bg-stone-400'
          }`}
        />
      ))}
    </div>
  );
}

export default function StarFeatureModal() {
  const [open, setOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const { user } = useAuth();
  const { checkFeature } = usePlan();
  const navigate = useNavigate();

  useEffect(() => {
    // Only show for logged-in users who haven't dismissed it
    if (!user) return;
    const dismissed = localStorage.getItem(STORAGE_KEY);
    if (!dismissed) {
      // Small delay so the app loads first
      const timer = setTimeout(() => setOpen(true), 1500);
      return () => clearTimeout(timer);
    }
  }, [user]);

  const handleDismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setOpen(false);
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleGetStarted = (path) => {
    handleDismiss();
    navigate(path);
  };

  if (!open) return null;

  const step = STEPS[currentStep];
  const StepIcon = step.icon;
  const isLast = currentStep === STEPS.length - 1;
  const hasFeature = checkFeature('agentic_personalization');

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={handleDismiss} />

      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-300">
        {/* Top accent bar */}
        <div className="h-1.5 bg-gradient-to-r from-violet-500 via-blue-500 to-emerald-500" />

        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1.5 text-stone-400 hover:text-stone-600 rounded-lg hover:bg-stone-100 transition-colors z-10"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Star badge */}
        <div className="absolute top-4 left-4 flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 border border-amber-200 rounded-full">
          <Star className="w-3 h-3 text-amber-500 fill-amber-500" />
          <span className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">New Feature</span>
        </div>

        {/* Content */}
        <div className="px-8 pt-14 pb-6">
          {/* Icon */}
          <div className={`w-14 h-14 rounded-2xl ${step.iconBg} flex items-center justify-center mb-5`}>
            <StepIcon className={`w-7 h-7 ${step.iconColor}`} />
          </div>

          {/* Title */}
          <h2 className="text-xl font-bold text-stone-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {step.title}
          </h2>
          <p className="text-sm text-violet-600 font-medium mb-3">{step.subtitle}</p>
          <p className="text-sm text-stone-600 leading-relaxed mb-5">{step.description}</p>

          {/* Step-specific content */}
          {step.highlights && (
            <div className="space-y-3 mb-5">
              {step.highlights.map((h, i) => {
                const HIcon = h.icon;
                return (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center flex-shrink-0">
                      <HIcon className="w-4 h-4 text-violet-500" />
                    </div>
                    <p className="text-sm text-stone-700 pt-1">{h.text}</p>
                  </div>
                );
              })}
            </div>
          )}

          {step.contextCards && (
            <div className="grid grid-cols-2 gap-2.5 mb-5">
              {step.contextCards.map((card, i) => (
                <div key={i} className={`border rounded-xl p-3 ${card.color}`}>
                  <p className="text-xs font-bold text-stone-800 mb-0.5">{card.label}</p>
                  <p className="text-[11px] text-stone-500">{card.desc}</p>
                </div>
              ))}
            </div>
          )}

          {step.actions && (
            <div className="space-y-2.5 mb-5">
              {step.actions.map((action, i) => (
                <div key={i} className="flex items-center gap-3 bg-stone-50 rounded-lg px-3 py-2.5">
                  <Check className={`w-4 h-4 ${action.color} flex-shrink-0`} />
                  <div>
                    <p className="text-xs font-bold text-stone-800">{action.label}</p>
                    <p className="text-[11px] text-stone-500">{action.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step.getStartedSteps && (
            <div className="bg-stone-50 rounded-xl p-4 mb-5">
              <ol className="space-y-2.5">
                {step.getStartedSteps.map((s, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-violet-100 text-violet-700 text-xs font-bold flex items-center justify-center flex-shrink-0">
                      {i + 1}
                    </span>
                    <p className="text-sm text-stone-700 pt-0.5">{s}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 pb-6 flex items-center justify-between">
          <StepDots currentStep={currentStep} totalSteps={STEPS.length} onDotClick={setCurrentStep} />

          <div className="flex items-center gap-2">
            {currentStep > 0 && (
              <Button size="sm" variant="outline" onClick={handlePrev} className="text-xs">
                Back
              </Button>
            )}

            {isLast ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={handleDismiss} className="text-xs">
                  Maybe Later
                </Button>
                {hasFeature ? (
                  <>
                    <Button
                      size="sm"
                      onClick={() => handleGetStarted('/ai-reviews')}
                      className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
                    >
                      <ShieldCheck className="w-3 h-3 mr-1" />
                      Open AI Reviews
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => handleGetStarted('/campaigns')}
                      className="text-xs bg-stone-800 hover:bg-stone-900 text-white"
                    >
                      <ArrowRight className="w-3 h-3 mr-1" />
                      Build a Campaign
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => handleGetStarted('/admin/plan-usage')}
                    className="text-xs bg-violet-600 hover:bg-violet-700 text-white"
                  >
                    <ArrowRight className="w-3 h-3 mr-1" />
                    Upgrade to Pro
                  </Button>
                )}
              </div>
            ) : (
              <Button
                size="sm"
                onClick={handleNext}
                className="text-xs bg-stone-800 hover:bg-stone-900 text-white"
              >
                Next
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
